<template>
  <div>
    <v-col cols="12" :class="{ hidden: !question.enabled }" class="question">
      <collapsible
        :title="question.label || 'New field' + (value && value.clientId ? ' for ' + value.label : '')"
        class="collapsed-field"
        :onOpen="onCollapsibleOpen"
      >
        <v-row align="center" dense>
          <v-col cols="12" class="py-0">
            <v-text-field variant="underlined" required :rules="requiredRules" label="Field" v-model="question.label" auto-grow></v-text-field>
          </v-col>
          <v-col cols="5" class="py-0">
            <v-select
              label="Response Type"
              variant="underlined"
              item-title="value"
              item-value="key"
              v-model="question.type"
              :items="answerTypes"
              required
              @change="onResponseTypeChange(question)"
              :rules="requiredRules"
            ></v-select>
          </v-col>
          <v-col cols="4" class="py-0">
            <v-text-field
              required
              variant="underlined"
              :rules="requiredRules"
              type="number"
              min="0"
              label="Display Order"
              v-model="question.order"
            ></v-text-field>
          </v-col>
          <v-col cols="1" class="py-0 d-flex">
            <v-tooltip :text="question.enabled ? 'Hide' : 'Show'" location="top">
              <template v-slot:activator="{ props }">
                <v-icon @click="toggleQuestion(question)" v-bind="props">{{
                  question.enabled ? 'mdi-eye-outline' : 'mdi-eye-off-outline'
                }}</v-icon>
              </template>
            </v-tooltip>
          </v-col>
          <v-col cols="1" class="py-0 mx-auto">
            <v-icon @click="removeQuestion(question)" color="red">mdi-delete-outline</v-icon>
          </v-col>
        </v-row>
        <v-row v-if="question.type && question.type === 'text'" class="answer" dense>
          <v-col cols="12">
            <v-switch v-model="question.required" label="Required"></v-switch>
          </v-col>
        </v-row>
        <v-row v-if="question.type && (question.type === 'multiselect' || question.type === 'dropdown')" class="answer" dense>
          <v-col cols="12">
            <label class="subtitle">Responses</label>
            <v-icon class="px-4" @click="addAnswer">mdi-plus-circle-outline</v-icon>
            <a @click.prevent="showBulkAddAnswers" href="#" class="px-4">Add in Bulk</a>
          </v-col>
          <v-col
            cols="12"
            class="py-0"
            :class="[{ hidden: !value.enabled }, 'py-0']"
            v-for="value in question.values"
            :key="value.clientId"
          >
            <v-row align="center" dense>
              <v-col cols="5" class="py-0">
                <v-text-field required :rules="requiredRules" label="Response" v-model="value.label" auto-grow></v-text-field>
              </v-col>
              <v-col cols="4" sm="3" class="py-0">
                <v-text-field
                  required
                  :rules="requiredRules"
                  type="number"
                  min="0"
                  label="Display Order"
                  v-model="value.order"
                ></v-text-field>
              </v-col>
              <v-col cols="1" class="py-0 d-flex">
                <v-tooltip :text="value.enabled ? 'Hide' : 'Show'" location="top">
                  <template v-slot:activator="{ props }">
                    <v-icon @click="toggleAnswer(value)" v-bind="props">{{ value.enabled ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}</v-icon>
                  </template>
                </v-tooltip>
              </v-col>
              <v-col cols="1" class="py-0">
                <v-icon @click="removeAnswer(value)" color="red">mdi-delete-outline</v-icon>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </collapsible>
    </v-col>

    <v-dialog persistent v-model="showBulkAddDialog" max-width="700">
      <BulkAddAnswer
        v-if="showBulkAddDialog"
        @add="bulkAddAnswers"
        @close="showBulkAddDialog = false"
        :question="question"
      ></BulkAddAnswer>
    </v-dialog>
  </div>
</template>
<script>
import SimpleCollapsibleSection from '@/components/SimpleCollapsibleSection'
import BulkAddAnswer from './BulkAddAnswer'
import { AnswerTypes } from '@/common/types'
import shortid from 'shortid'

export default {
  name: 'Question',
  props: {
    question: Object,
    validate: Function,
    value: Object
  },
  components: {
    collapsible: SimpleCollapsibleSection,
    BulkAddAnswer
  },
  data() {
    return {
      answerTypes: Object.values(AnswerTypes.properties),
      showBulkAddDialog: false,
      requiredRules: [v => (!v || (v && !v.toString()) || (v && !v.toString().trim()) ? 'Required' : true)]
    }
  },
  computed: {
    answerType() {
      return this.answerTypes.find(a => a.key === this.question.type) || {}
    },
    questions() {
      return this.$_.orderBy((this.value || {}).questions, ['order'], ['asc'])
    }
  },
  updated() {
    this.validate()
  },
  methods: {
    onCollapsibleOpen() {
      this.validate()
    },
    onResponseTypeChange(question) {
      if (question.type === 'text') {
         question.values = []
         question.required = question.required ? question.required : true
      }
      if (question.type === 'multiselect') {
        question.required = true
        if (!AnswerTypes.properties[AnswerTypes.MULTISELECT].dependants) {
          for (let value of question.values) {
            value.questions = []
          }
        }
      }
      if(question.type === 'dropdown') {
        question.required = true
      }
    },
    addAnswer() {
      this.question.values.push({
        clientId: new Date().getTime(),
        label: '',
        order: this.question.values.length + 1,
        enabled: true,
        questions: []
      })
    },
    bulkAddAnswers(lines) {
      for (let line of lines) {
        if (this.question.values.find(v => v.label.trim() === line)) {
          continue
        }
        this.question.values.push({
          clientId: shortid.generate(),
          label: line,
          order: this.question.values.length + 1,
          enabled: true,
          questions: []
        })
      }
      this.showBulkAddDialog = false
    },
    showBulkAddAnswers() {
      this.showBulkAddDialog = true
    },
    removeAnswer(value) {
      this.question.values = this.question.values.filter(a => a.clientId !== value.clientId)
      this.$emit('onDelete', value, 'value')
    },
    addQuestion(value) {
      value.questions.push({
        clientId: new Date().getTime(),
        label: '',
        order: value.questions.length + 1,
        enabled: true,
        values: []
      })
    },
    toggleAnswer(value) {
      value.enabled = !value.enabled
    },
    toggleQuestion(question) {
      question.enabled = !question.enabled
    },
    removeQuestion(question, value) {
      if (value) {
        value.questions = value.questions.filter(q => q.clientId !== question.clientId)
        return
      }

      this.$emit('onDelete', question, 'question')
      this.$emit('removeQuestion', question)
    }
  }
}
</script>

<style scoped>
.subtitle {
  text-decoration: underline;
}

.v-icon:hover {
  color: black;
}

.hidden {
  background: #eee;
}

.collapsed-field span {
  font-weight: bold;
}
</style>
